<script lang="ts" setup>
import H3Heading from "~/components/H3Heading.vue";
import LookInside from "~/components/product/LookInside.vue";
import H2Heading from "~/components/H2Heading.vue";
import type ProductType from "~/types/Product";
import ModalBox from "~/components/ModalBox.vue";
import ButtonSecondary from "~/components/ButtonSecondary.vue";
import ButtonPrimary from "~/components/ButtonPrimary.vue";
import type { PropType } from "vue";

// Properties
const props = defineProps({
    product: {
        type: Object as PropType<ProductType>,
        default: null,
    },
    code: {
        type: String,
        default: "",
    },
    resourceCode: {
        type: Object,
        default: null,
    },
    trialType: {
        type: String,
        default: "",
    },
});

const includedDigital = computed(() => {
    return !!props.code;
});

// Data
const config = useRuntimeConfig();
const showLookInside = ref(false);
const showModalBox: Ref<boolean> = ref(false);

// Functions
async function copyCodeToClipboard() {
    //document.execCommand(props.code);
    await navigator.clipboard.writeText(props.code);
    showModalBox.value = true;
}
</script>

<template>
    <div class="rounded-sm border p-4 md:flex">
        <div class="flex-none lg:pr-6">
            <div class="relative" @mouseover="showLookInside = true" @mouseleave="showLookInside = false">
                <NuxtLink :to="`/products/${product.c_slug}`">
                    <img
                        :src="product.photo_lg"
                        :srcset="`${product.photo_lg} 1x, ${product.photo_lg_2x} 2x`"
                        width="205"
                        height="265"
                        :alt="'Cover Image ' + product.title"
                        class="mx-auto w-1/2 border border-gray-300 shadow-md shadow-gray-400 sm:w-3/5 md:w-auto"
                    />
                    <div class="absolute bottom-2 w-full text-center md:bottom-4">
                        <LookInside v-model:show-button="showLookInside" :product="product" />
                    </div>
                </NuxtLink>
            </div>
        </div>
        <div class="flex-auto pl-4 lg:flex lg:pl-0">
            <div class="flex-1">
                <NuxtLink :to="`/products/${product.c_slug}`">
                    <H2Heading
                        line-height="leading-h4"
                        size="text-xl md:text-2xl"
                        class="text-abc-blue mt-4 mb-4 text-center md:mt-0 md:mb-8 md:text-left"
                        >{{ product.title }}
                    </H2Heading>
                </NuxtLink>
                <div v-if="trialType" class="font-bold">Your order includes:</div>
                <ul>
                    <li
                        v-if="trialType?.toLowerCase().includes('digital') || trialType?.toLowerCase().includes('both')"
                    >
                        1 code for access to eBook and online testing through CourseWave
                    </li>
                    <li v-if="trialType?.toLowerCase().includes('print') || trialType?.toLowerCase().includes('both')">
                        1 print preview workbook
                    </li>
                </ul>
            </div>
            <div v-if="code" class="relative mt-4 flex-1 pl-4 text-right lg:mt-0 lg:pl-0">
                <H3Heading>My code: {{ code }}</H3Heading>
                <div v-if="resourceCode?.expires_on">
                    <p>Please use prior to expiration.</p>
                    <p>Expires on {{ new Date(resourceCode.expires_on).toLocaleDateString() }}</p>
                </div>
                <div v-else>Free trial will expire {{ resourceCode.valid_for_days }} days<br />after activation</div>

                <ButtonSecondary
                    type="link"
                    :href="`${config.public.CW_HOST}/login?c=${code}`"
                    target="_blank"
                    class="mt-6"
                >
                    Activate code in CourseWave
                </ButtonSecondary>
                <br />
                <ButtonPrimary class="absolute right-0 bottom-0 w-64" @click="copyCodeToClipboard"
                    >Copy code to clipboard
                </ButtonPrimary>
            </div>
        </div>

        <ModalBox v-model:show-modal-box="showModalBox" width="w-11/12 md:w-2/5">
            <H3Heading class="text-center">Code copied!</H3Heading>
            <div class="mt-4 text-center">
                Now go to
                <a :href="`${config.public.CW_HOST}/login`" target="_blank" class="text-abc-blue">CourseWave.com</a>
                to start your free trial!
            </div>
            <div class="mt-10 text-center">
                <ButtonPrimary class="mt-2 w-full" @click="showModalBox = false"> OK </ButtonPrimary>
            </div>
        </ModalBox>
    </div>
</template>
